/**
 * NOTIFY ME for a sold-out size (markup in snippets/restock-notify.liquid).
 *
 * Shows while the selected variant is unavailable. It opens or closes the moment a size is tapped,
 * from the availability rendered with the page, and re-checks on every variant:update.
 * Submitting makes three Klaviyo client calls with the public key only:
 *   1. subscriptions — SMS consent and the Restock Waitlist (SMS) list. Back in Stock texts only
 *      go to SMS subscribers, so this one has to succeed before the other two.
 *   2. events — "Requested Restock" with the product and size, which the segments filter on.
 *   3. back-in-stock-subscriptions — Klaviyo's own waitlist for this variant; its flow texts
 *      them when inventory comes back.
 * Success shows once call 1 succeeds; failures of 2 and 3 are only logged.
 */

const KLAVIYO = { key: 'UJmr82', list: 'VCaVuw', revision: '2026-07-15' };
const MOTION_MS = 200;
const SUBMIT_LABEL = 'NOTIFY ME';
const MESSAGES = {
  invalid: 'Enter a valid US phone number.',
  failed: 'Something went wrong. Try again.',
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Posts to a Klaviyo client endpoint. Retries once on 429, 5xx or a network error.
 * @param {string} path
 * @param {object} data
 * @returns {Promise<boolean>} true on success (Klaviyo answers 202 with an empty body)
 */
async function klaviyoPost(path, data) {
  for (let attempt = 0; attempt < 2; attempt++) {
    if (attempt) await wait(1000);
    try {
      const response = await fetch(`https://a.klaviyo.com/client/${path}?company_id=${KLAVIYO.key}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO.revision },
        body: JSON.stringify({ data }),
      });
      if (response.ok) return true;
      if (response.status !== 429 && response.status < 500) {
        console.warn('Klaviyo', path, response.status);
        return false;
      }
    } catch (error) {
      console.warn('Klaviyo', path, error);
    }
  }
  return false;
}

/**
 * US numbers only, as E.164. Area codes and exchanges never start with 0 or 1, so those are
 * typos and are stopped here rather than rejected by Klaviyo.
 * @param {string} raw
 * @returns {string | null}
 */
function toE164(raw) {
  let digits = String(raw || '').replace(/\D/g, '');
  if (digits.length === 11 && digits[0] === '1') digits = digits.slice(1);
  return /^[2-9]\d{2}[2-9]\d{6}$/.test(digits) ? `+1${digits}` : null;
}

/** @param {string | null | undefined} url */
const absolute = (url) => (url && url.startsWith('//') ? `https:${url}` : url || '');

class RestockNotify extends HTMLElement {
  /** @type {{ product: any, variants: any[] }} */
  #data = { product: {}, variants: [] };
  /** @type {any} */
  #variant = null;
  #timer = 0;
  #request = 0;
  #sending = false;
  /** @type {HTMLElement | null} */
  #picker = null;

  connectedCallback() {
    if (this.dataset.ready) return;
    if (this.closest('.quick-add-modal')) {
      this.hidden = true;
      return;
    }
    this.dataset.ready = 'true';

    try {
      this.#data = JSON.parse(this.querySelector('[data-restock-data]')?.textContent || '');
    } catch (error) {
      console.warn('restock-notify: no product data', error);
      return;
    }

    // The variant picker re-renders on any change that reaches it, and Enter in a field would
    // submit its form. Neither should happen from in here.
    const stop = (/** @type {Event} */ event) => event.stopPropagation();
    this.addEventListener('change', stop);
    this.addEventListener('input', stop);
    this.#phone.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      this.#submit();
    });

    this.#button.addEventListener('click', () => this.#submit());
    this.#toggle.addEventListener('click', () => this.#toggleTerms());
    document.addEventListener('variant:update', this.#onVariantUpdate);
    this.#picker = this.closest('variant-picker');
    this.#picker?.addEventListener('change', this.#onOptionChange);

    this.#variant = this.#find(this.dataset.variantId);
    if (!this.hidden) {
      this.classList.add('is-open');
      this.#reset();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('variant:update', this.#onVariantUpdate);
    this.#picker?.removeEventListener('change', this.#onOptionChange);
  }

  get #phone() {
    return /** @type {HTMLInputElement} */ (this.querySelector('[data-restock-phone]'));
  }

  get #button() {
    return /** @type {HTMLButtonElement} */ (this.querySelector('[data-restock-submit]'));
  }

  get #toggle() {
    return /** @type {HTMLButtonElement} */ (this.querySelector('[data-restock-toggle]'));
  }

  /** @param {string | number | undefined} id */
  #find(id) {
    return this.#data.variants.find((variant) => String(variant.id) === String(id)) || null;
  }

  /**
   * A size or colour was tapped. The picker only reports the new variant once its section fetch
   * is back (about 0.6s), so the variant is worked out here from the checked options instead, and
   * the block starts moving straight away. variant:update corrects it if the server disagrees.
   * @param {Event} event
   */
  #onOptionChange = (event) => {
    if (!this.#picker || !(event.target instanceof Element)) return;

    // data-input-id is "<option position>-<value index>" on radios and on <option>s alike.
    /** @type {Map<number, string>} */
    const selected = new Map();
    for (const field of this.#picker.querySelectorAll('input[type="radio"]:checked, select')) {
      const input = /** @type {HTMLInputElement | HTMLSelectElement} */ (field);
      const source = input instanceof HTMLSelectElement ? input.selectedOptions[0] : input;
      const position = Number.parseInt(source?.dataset.inputId || '', 10);
      if (position > 0) selected.set(position, input.value);
    }
    if (!selected.size) return;

    const variant = this.#data.variants.find((candidate) =>
      Array.from(selected).every(([position, value]) => candidate.options?.[position - 1] === value)
    );
    if (variant) this.#apply({ ...variant });
  };

  /** @param {Event} event */
  #onVariantUpdate = (event) => {
    const { resource, data } = /** @type {CustomEvent} */ (event).detail || {};
    if (!resource || String(data?.productId) !== this.dataset.productId) return;

    // Availability comes from the fresh variant; the rest from the data rendered with the page.
    const variant = { ...(this.#find(resource.id) || { title: resource.title, price: resource.price }) };
    variant.id = resource.id;
    variant.available = resource.available;
    this.#apply(variant);
  };

  /** Open for an unavailable variant, close for any other. @param {any} variant */
  #apply(variant) {
    const changed = String(variant.id) !== String(this.#variant?.id);
    this.#variant = variant;

    if (variant.available !== false) {
      this.#close();
      return;
    }
    if (changed || this.hidden) this.#reset();
    this.#open();
  }

  #open() {
    clearTimeout(this.#timer);
    this.classList.remove('is-closing');
    if (!this.hidden && this.classList.contains('is-open')) return;

    this.hidden = false;
    this.classList.add('is-moving');
    void this.offsetHeight; // start the height from 0fr
    this.classList.add('is-open');
    this.#timer = window.setTimeout(() => this.classList.remove('is-moving'), MOTION_MS);
  }

  #close() {
    // Already on its way: the variant:update that follows a tap must not restart the timer.
    if (this.hidden || this.classList.contains('is-closing')) return;
    clearTimeout(this.#timer);
    this.classList.add('is-moving', 'is-closing');
    this.classList.remove('is-open');
    this.#timer = window.setTimeout(() => {
      this.hidden = true;
      this.classList.remove('is-moving', 'is-closing');
    }, MOTION_MS);
  }

  /** Back to the form for the current size. The number typed in is kept. */
  #reset() {
    this.#request++;
    this.#sending = false;
    this.#button.disabled = false;
    this.#button.textContent = SUBMIT_LABEL;
    this.#showError('');
    this.#el('[data-restock-form]').hidden = false;
    this.#el('[data-restock-success]').hidden = true;
  }

  /** @param {string} selector */
  #el(selector) {
    return /** @type {HTMLElement} */ (this.querySelector(selector));
  }

  /** @param {string} message */
  #showError(message) {
    const error = this.#el('[data-restock-error]');
    error.textContent = message;
    error.hidden = !message;
  }

  #toggleTerms() {
    const open = this.#toggle.getAttribute('aria-expanded') !== 'true';
    this.#toggle.setAttribute('aria-expanded', String(open));
    this.#toggle.textContent = open ? 'Hide' : 'Terms';
    this.#el('[data-restock-terms]').classList.toggle('is-open', open);
  }

  #succeed() {
    const hadFocus = this.contains(document.activeElement);
    this.#el('[data-restock-size]').textContent = this.#variant?.size || this.#variant?.title || '';
    this.#el('[data-restock-form]').hidden = true;
    const success = this.#el('[data-restock-success]');
    success.hidden = false;
    if (hadFocus) success.focus({ preventScroll: true });
  }

  async #submit() {
    if (this.#sending || !this.#variant) return;

    // Bots fill in the hidden field: look done, send nothing.
    if (/** @type {HTMLInputElement} */ (this.#el('[data-restock-hp]')).value) {
      this.#succeed();
      return;
    }

    const phone = toE164(this.#phone.value);
    if (!phone) {
      this.#showError(MESSAGES.invalid);
      this.#phone.focus();
      return;
    }

    this.#showError('');
    this.#sending = true;
    this.#button.disabled = true;
    this.#button.textContent = '…';
    const request = ++this.#request;
    const variant = this.#variant;

    const subscribed = await this.#subscribe(phone);
    // A size change while this was in flight has already reset the block.
    if (request !== this.#request) return;

    this.#sending = false;
    this.#button.disabled = false;
    this.#button.textContent = SUBMIT_LABEL;

    if (!subscribed) {
      this.#showError(MESSAGES.failed);
      return;
    }

    this.#succeed();
    this.#tagAndWaitlist(phone, variant);
  }

  /** Call 1: SMS consent and the restock list. @param {string} phone */
  #subscribe(phone) {
    return klaviyoPost('subscriptions', {
      type: 'subscription',
      attributes: {
        custom_source: 'Restock Waitlist',
        profile: {
          data: {
            type: 'profile',
            attributes: {
              phone_number: phone,
              subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } },
            },
          },
        },
      },
      relationships: { list: { data: { type: 'list', id: KLAVIYO.list } } },
    });
  }

  /**
   * Calls 2 and 3, in parallel.
   * @param {string} phone
   * @param {any} variant
   */
  async #tagAndWaitlist(phone, variant) {
    const { product } = this.#data;
    const profile = { data: { type: 'profile', attributes: { phone_number: phone } } };

    const [tagged, waitlisted] = await Promise.all([
      klaviyoPost('events', {
        type: 'event',
        attributes: {
          metric: { data: { type: 'metric', attributes: { name: 'Requested Restock' } } },
          profile,
          properties: {
            product_title: product.title,
            product_handle: product.handle,
            product_id: product.id,
            variant_id: variant.id,
            variant_title: variant.title,
            size: variant.size || '',
            color: variant.color || '',
            price: (Number(variant.price) / 100).toFixed(2),
            url: `${product.url}?variant=${variant.id}`,
            image_url: absolute(variant.image || product.image),
            source: 'pdp_inline',
          },
        },
      }),
      klaviyoPost('back-in-stock-subscriptions', {
        type: 'back-in-stock-subscription',
        attributes: { channels: ['SMS'], profile },
        relationships: {
          variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variant.id}` } },
        },
      }),
    ]);

    if (!tagged) console.error('restock-notify: the Requested Restock event was not recorded');
    if (!waitlisted) console.error('restock-notify: the Back in Stock subscription was not created');
  }
}

if (!customElements.get('restock-notify')) {
  customElements.define('restock-notify', RestockNotify);
}
